import { useCallback, useMemo, type ReactNode, type Ref } from "react";
import type { BoardLaneDef } from "./Board.types";
import { BOARD_LANE_OVERSCAN } from "./boardWindowing";
import { BoardLaneWindowContext, useBoardWindow } from "./useBoardWindow";
import laneStyles from "./BoardLanes.module.scss";

/** Refs a lane block hands its elements. */
export interface BoardLaneRefs {
    /** Set on windowed lanes: measures the block, which needs `data-index`. */
    measureRef?: (node: HTMLElement | null) => void;
    headerRef?: Ref<HTMLDivElement>;
}

interface BoardWindowedLanesProps {
    lanes: readonly BoardLaneDef[];
    estimateLaneSize: (laneIndex: number) => number;
    /** Lanes that stay mounted off-screen: the dragged card's. */
    pinnedLaneIds: readonly string[];
    /** Goes to the first mounted lane's header, whichever lane that is. */
    firstLaneHeaderRef?: Ref<HTMLDivElement>;
    renderLane: (
        lane: BoardLaneDef,
        laneIndex: number,
        refs: BoardLaneRefs,
    ) => ReactNode;
}

/** Lane blocks of a large board: only those near the viewport mount, measured. */
export const BoardWindowedLanes = ({
    lanes,
    estimateLaneSize,
    pinnedLaneIds,
    firstLaneHeaderRef,
    renderLane,
}: Readonly<BoardWindowedLanesProps>) => {
    const getItemKey = useCallback((index: number) => lanes[index].id, [lanes]);
    const pinnedIndexes = useMemo(() => {
        const pinned = new Set(pinnedLaneIds);
        return lanes.flatMap((lane, index) =>
            pinned.has(lane.id) ? [index] : [],
        );
    }, [lanes, pinnedLaneIds]);
    const { listRef, runs, isKeyMounted, measureElement } = useBoardWindow({
        placement: null,
        count: lanes.length,
        getItemKey,
        estimateSize: estimateLaneSize,
        gap: 0,
        overscan: BOARD_LANE_OVERSCAN,
        chunk: 1,
        pinnedIndexes,
    });
    const firstMounted = runs.find((run) => run.kind === "item");

    return (
        <BoardLaneWindowContext.Provider value={isKeyMounted}>
            <div ref={listRef} className={laneStyles.lanes}>
                {runs.map((run) =>
                    run.kind === "spacer" ? (
                        <div
                            key={run.key}
                            aria-hidden
                            className={laneStyles.laneSpacer}
                            style={{ height: run.size }}
                        />
                    ) : (
                        renderLane(lanes[run.index], run.index, {
                            measureRef: measureElement,
                            headerRef:
                                run === firstMounted
                                    ? firstLaneHeaderRef
                                    : undefined,
                        })
                    ),
                )}
            </div>
        </BoardLaneWindowContext.Provider>
    );
};
